const SESSION_KEY = "tongxin.guandan.session.v1";

export interface PlayerSession {
  readonly token: string;
  readonly userId: string;
  readonly email: string;
  readonly nickname?: string;
  readonly kind?: "nickname" | "owner";
  readonly expiresAt: number;
}

export function saveSession(session: PlayerSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getSession(): PlayerSession | null {
  try {
    const legacy = sessionStorage.getItem(SESSION_KEY);
    const raw = localStorage.getItem(SESSION_KEY) ?? legacy;
    if (!raw) return null;
    if (legacy && !localStorage.getItem(SESSION_KEY)) {
      localStorage.setItem(SESSION_KEY, legacy);
      sessionStorage.removeItem(SESSION_KEY);
    }
    const value = JSON.parse(raw) as Partial<PlayerSession>;
    if (
      typeof value.token !== "string" ||
      typeof value.userId !== "string" ||
      typeof value.email !== "string" ||
      typeof value.expiresAt !== "number" ||
      value.expiresAt <= Date.now()
    ) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return value as PlayerSession;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function getSessionToken(): string | null {
  return getSession()?.token ?? null;
}

/** Records bounded active time without extending activity while the PWA is hidden. */
export async function heartbeatSession(signal?: AbortSignal): Promise<boolean> {
  const session = getSession();
  const configuredEndpoint = (import.meta.env.VITE_GAME_API_URL ??
    import.meta.env.VITE_AUTH_API_URL) as string | undefined;
  if (!session || !configuredEndpoint || session.token.startsWith("local:"))
    return false;
  const base = configuredEndpoint.replace(/\/$/u, "");
  const prefix = base.endsWith("/auth") ? base : `${base}/auth`;
  try {
    const response = await fetch(`${prefix}/session`, {
      headers: { authorization: `Bearer ${session.token}` },
      ...(signal ? { signal } : {}),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}

/** Revokes the provider session when available and always clears the browser copy. */
export async function revokeSession(): Promise<boolean> {
  const session = getSession();
  if (!session) {
    clearSession();
    return true;
  }
  const configuredEndpoint = (import.meta.env.VITE_GAME_API_URL ??
    import.meta.env.VITE_AUTH_API_URL) as string | undefined;
  if (!configuredEndpoint || session.token.startsWith("local:")) {
    clearSession();
    return true;
  }
  const base = configuredEndpoint.replace(/\/$/u, "");
  const prefix = base.endsWith("/auth") ? base : `${base}/auth`;
  try {
    const response = await fetch(`${prefix}/session`, {
      method: "DELETE",
      headers: { authorization: `Bearer ${session.token}` },
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearSession();
  }
}
